/**
 * CDR (Cloud Detection & Response) API module.
 *
 * Real per-domain split — this file used to be a stub re-export from `../api`.
 * The exports below were extracted verbatim from the monolithic
 * `frontend/src/services/api.ts` so consumers can import them directly from
 * `services/api/cdr` without round-tripping through the monolithic file.
 * Behavior is unchanged.
 *
 * Domain objects:
 *   - cdrApi               (alerts, anomalies, incidents, forensics, playbooks,
 *                           threat hunting, SOAR, identity threats, UEBA,
 *                           runtime rules, sensors, FIM, baselines, MITRE
 *                           coverage, investigation graph, threat center)
 *   - realtimeCSPMApi      (real-time CSPM detection rules + events + stats)
 *   - siemExportApi        (CDR SIEM connector + export + webhook management)
 *   - eventStreamApi       (event-stream config + ingest + recent events)
 *   - cdrLogAnalyticsApi   (log sources, events, sigma rules, detection alerts,
 *                           parsers, retention, OCSF normalization, K8s audit)
 *   - cdrAdvancedApi       (detection-as-code, saved queries, query exec/lang,
 *                           SIEM connectors, normalization, sigma evaluate)
 *   - threatIntelFeedsApi  (TI feed mgmt, IOCs, enrichment, MITRE ATT&CK)
 *   - preventionApi        (CDR prevention/blocking policies + actions + stats)
 *   - forensicTimelineApi  (forensic timeline build/get/annotate/export)
 *   - investigationApi     (CIRA: create/list/get + evidence/timeline/playbooks)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

export const cdrApi = {
  // Threat Intelligence
  getThreatFeeds: async (): Promise<any[]> => {
    // O4 (2026-07-05): the handler returns a `{feeds:[...],total:5}` envelope,
    // not a bare array — so Array.isArray() on the raw result was false and the
    // "Latest Published Advisories" widget rendered empty. Unwrap the envelope.
    const r = _unwrap<any>(await fetchClient.get<any>('/api/v1/cdr/threat-intel/feeds'));
    return Array.isArray(r) ? r : (r?.feeds ?? []);
  },

  getIOCs: async (params?: { type?: string; active?: boolean }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intel/iocs', params as any));
  },

  // Anomaly Detection
  getAnomalies: async (params?: { severity?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/anomalies', params as any));
  },

  getAnomaly: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/anomalies/${id}`));
  },

  // Incident Response
  getIncidents: async (params?: { status?: string; severity?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incidents', params as any));
  },

  getIncident: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incidents/${id}`));
  },

  createIncident: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/incidents', data));
  },

  updateIncidentStatus: async (id: string, status: string): Promise<any> => {
    return _unwrap(await fetchClient.patch<any>(`/api/v1/cdr/incidents/${id}`, { status }));
  },

  // Forensics
  getInvestigations: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/forensics/investigations'));
  },

  createInvestigation: async (incidentId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/forensics/investigations', {
      incident_id: incidentId,
    }));
  },

  getEvidence: async (investigationId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/forensics/investigations/${investigationId}/evidence`));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/dashboard'));
  },

  // Alerts
  getAlerts: async (params?: { severity?: string; status?: string; time_range?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/alerts', params as any));
  },

  // Cleaned aggregator feed — the canonical "active detections" surface. The
  // cdr-aggregator excludes is_rule_misfire (behavioral false positives) and
  // is_self_scan rows, so this reflects genuine threats (Wiz parity). Returns
  // the detections array regardless of {detections:[...]} vs bare-array shape.
  getActiveDetections: async (params?: { severity?: string; page?: number; page_size?: number }): Promise<any[]> => {
    const res = await _unwrap(await fetchClient.get<any>('/api/v1/cdr/detections', params as any));
    return Array.isArray(res) ? res : (res?.detections ?? res?.items ?? []);
  },

  getAlert: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/alerts/${id}`));
  },

  updateAlertStatus: async (id: string, status: string): Promise<any> => {
    return _unwrap(await fetchClient.patch<any>(`/api/v1/cdr/alerts/${id}`, { status }));
  },

  correlateAlert: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/alerts/${id}/correlate`));
  },

  // Forensic Events
  getForensicEvents: async (params?: { time_range?: string; event_type?: string; source?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/forensics/events', params as any));
  },

  getEventTimeline: async (resourceId: string, timeRange?: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/forensics/timeline/${resourceId}`, { time_range: timeRange } as any));
  },

  // Response Playbooks
  getResponsePlaybooks: async (params?: { status?: string; trigger_type?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/playbooks', params as any));
  },

  getResponsePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/playbooks/${id}`));
  },

  createResponsePlaybook: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/playbooks', data));
  },

  updateResponsePlaybook: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/playbooks/${id}`, data));
  },

  executePlaybook: async (id: string, params?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/playbooks/${id}/execute`, params));
  },

  togglePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/playbooks/${id}/toggle`));
  },

  deletePlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/playbooks/${id}`));
  },

  // Threat Hunting
  getHuntCampaigns: async (params?: { status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/hunt/campaigns', params as any));
  },

  getHuntCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/hunt/campaigns/${id}`));
  },

  createHuntCampaign: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/hunt/campaigns', data));
  },

  updateHuntCampaign: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/hunt/campaigns/${id}`, data));
  },

  pauseHuntCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/hunt/campaigns/${id}/pause`));
  },

  resumeHuntCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/hunt/campaigns/${id}/resume`));
  },

  getHuntResults: async (campaignId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/hunt/campaigns/${campaignId}/results`));
  },

  exportHuntResults: async (campaignId: string, format?: string): Promise<Blob> => {
    const response = await fetchClient.download(
      `/api/v1/cdr/hunt/campaigns/${campaignId}/export`,
      { format },
    );
    if (response.error || !response.data) {
      throw new Error(response.error || 'Export failed');
    }
    return response.data;
  },

  // Threat Feed Management
  addThreatFeed: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/threat-intel/feeds', data));
  },

  updateThreatFeed: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/threat-intel/feeds/${id}`, data));
  },

  deleteThreatFeed: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/threat-intel/feeds/${id}`));
  },

  syncThreatFeed: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/threat-intel/feeds/${id}/sync`));
  },

  // Incident Response sub-service
  getCDRIncidents: async (params?: { status?: string; severity?: string; assignee?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/incidents', params as any));
  },
  getCDRIncident: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/incidents/${id}`));
  },
  createCDRIncident: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/incident-response/incidents', data));
  },
  escalateIncident: async (id: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/incident-response/incidents/${id}/escalate`, data));
  },
  getCDRIncidentTimeline: async (id: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/incident-response/incidents/${id}/timeline`));
  },
  getCDRSOCMetrics: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/incident-response/soc-metrics', params as any));
  },

  // Threat Hunting sub-service
  getCDRHunts: async (params?: { status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-hunting/hunts', params as any));
  },
  getCDRHunt: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/threat-hunting/hunts/${id}`));
  },
  createCDRHunt: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/threat-hunting/hunts', data));
  },
  executeHunt: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/threat-hunting/hunts/${id}/execute`));
  },
  getCDRHuntFindings: async (id: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/threat-hunting/hunts/${id}/findings`));
  },
  getCDRMITRETechniques: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-hunting/mitre-techniques'));
  },

  // SOAR Automation sub-service
  getCDRPlaybooks: async (params?: { status?: string; trigger_type?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/playbooks', params as any));
  },
  getCDRPlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/soar/playbooks/${id}`));
  },
  executeCDRPlaybook: async (id: string, params?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/playbooks/${id}/execute`, params));
  },
  getCDRExecutions: async (params?: { playbook_id?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/executions', params as any));
  },
  getCDRPlaybookTemplates: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/templates'));
  },
  getCDRSOARMetrics: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/soar/metrics', params as any));
  },

  // Threat Intelligence sub-service
  getCDRFeeds: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intelligence/feeds'));
  },
  getCDRIOCs: async (params?: { type?: string; confidence_min?: number; tlp?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intelligence/iocs', params as any));
  },
  searchCDRIOCs: async (query: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intelligence/iocs/search', { query }));
  },
  lookupIOC: async (value: string, type?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/threat-intelligence/iocs/lookup', { value, type }));
  },
  getCDRThreatActors: async (params?: { active?: boolean }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intelligence/threat-actors', params as any));
  },
  getCDRThreatActor: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/threat-intelligence/threat-actors/${id}`));
  },
  getCDRCampaigns: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/threat-intelligence/campaigns'));
  },
  getCDRThreatIntelStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/threat-intelligence/stats'));
  },

  // Forensic Artifacts
  uploadForensicArtifact: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/forensics/artifacts/upload', data));
  },

  listForensicArtifacts: async (caseId?: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/forensics/artifacts', caseId ? { case_id: caseId } as any : undefined));
  },

  getForensicArtifact: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/forensics/artifacts/${id}`));
  },

  // Log Source Auto-Discovery
  autoDiscoverLogSources: async (accountId: string, provider: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/log-sources/auto-discover', {
      account_id: accountId,
      provider,
    }));
  },

  // ========================================================================
  // Incident Response - Kill Chain Mapping (port 8054)
  // ========================================================================

  getIncidentKillChain: async (incidentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/incidents/${incidentId}/kill-chain`));
  },

  getKillChainPhases: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/kill-chain/phases'));
  },

  getKillChainCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/incident-response/kill-chain/coverage'));
  },

  // ========================================================================
  // Incident Response - Automated Containment (port 8054)
  // ========================================================================

  getContainmentTemplates: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/containment/templates'));
  },

  getContainmentTemplate: async (templateId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/containment/templates/${templateId}`));
  },

  executeContainment: async (data: { template_id: string; target_resource: string; incident_id?: string; parameters?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/incident-response/containment/execute', data));
  },

  getContainmentExecutions: async (params?: { status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/containment/executions', params as any));
  },

  rollbackContainment: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/incident-response/containment/executions/${executionId}/rollback`));
  },

  // ========================================================================
  // Incident Response - Evidence Collection (port 8054)
  // ========================================================================

  getEvidenceWorkflows: async (params?: { incident_id?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/evidence-workflows', params as any));
  },

  createEvidenceWorkflow: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/incident-response/evidence-workflows', data));
  },

  getEvidenceWorkflow: async (workflowId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/evidence-workflows/${workflowId}`));
  },

  // ========================================================================
  // Incident Response - Timeline Reconstruction (port 8054)
  // ========================================================================

  getReconstructedTimeline: async (incidentId: string, params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/incidents/${incidentId}/reconstructed-timeline`, params as any));
  },

  // ========================================================================
  // Incident Response - Playbook Management (port 8054)
  // ========================================================================

  getIRPlaybooks: async (params?: { status?: string; category?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/playbooks', params as any));
  },

  getIRPlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/incident-response/playbooks/${id}`));
  },

  createIRPlaybook: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/incident-response/playbooks', data));
  },

  updateIRPlaybook: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/incident-response/playbooks/${id}`, data));
  },

  deleteIRPlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/incident-response/playbooks/${id}`));
  },

  toggleIRPlaybook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/incident-response/playbooks/${id}/toggle`));
  },

  getIRPlaybookTemplateLibrary: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/playbooks/templates/library'));
  },

  getIRPlaybookExecutions: async (params?: { playbook_id?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/playbook-executions', params as any));
  },

  // ========================================================================
  // Incident Response - SOC Metrics & Detection Rules (port 8054)
  // ========================================================================

  getIRSOCMetrics: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/incident-response/soc-metrics', params as any));
  },

  getIRDetectionRules: async (params?: { status?: string; category?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/incident-response/detection-rules', params as any));
  },

  // ========================================================================
  // SOAR - Playbook Templates (port 8056)
  // ========================================================================

  getSOARPlaybookTemplates: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/playbook-templates'));
  },

  getSOARPlaybookTemplate: async (templateId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/soar/playbook-templates/${templateId}`));
  },

  instantiateSOARPlaybookTemplate: async (templateId: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/playbook-templates/${templateId}/instantiate`, data));
  },

  // ========================================================================
  // SOAR - Webhook Management (port 8056)
  // ========================================================================

  getSOARWebhooks: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/webhooks'));
  },

  getSOARWebhook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/soar/webhooks/${id}`));
  },

  createSOARWebhook: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/soar/webhooks', data));
  },

  updateSOARWebhook: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/soar/webhooks/${id}`, data));
  },

  deleteSOARWebhook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/soar/webhooks/${id}`));
  },

  testSOARWebhook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/webhooks/${id}/test`));
  },

  toggleSOARWebhook: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/webhooks/${id}/toggle`));
  },

  // ========================================================================
  // SOAR - Response Orchestration (port 8056)
  // ========================================================================

  createOrchestrationPlan: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/soar/orchestration/plans', data));
  },

  getOrchestrationPlans: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/orchestration/plans'));
  },

  executeOrchestrationPlan: async (planId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/orchestration/plans/${planId}/execute`));
  },

  // ========================================================================
  // SOAR - Dashboard & Metrics (port 8056)
  // ========================================================================

  getSOARDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/soar/soar-dashboard'));
  },

  getSOARMetrics: async (params?: { time_range?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/soar/soar-metrics', params as any));
  },

  // ========================================================================
  // SOAR - Workflow Management (port 8056)
  // ========================================================================

  updateSOARWorkflow: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/soar/workflows/${id}`, data));
  },

  deleteSOARWorkflow: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/soar/workflows/${id}`));
  },

  getSOARWorkflowExecutions: async (id: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/soar/workflows/${id}/executions`));
  },

  // ========================================================================
  // SOAR - Integration Connectors (port 8056)
  // ========================================================================

  getSOARIntegrations: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/integrations'));
  },

  getSOARIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/soar/integrations/${id}`));
  },

  createSOARIntegration: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/soar/integrations', data));
  },

  updateSOARIntegration: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/soar/integrations/${id}`, data));
  },

  deleteSOARIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/soar/integrations/${id}`));
  },

  testSOARIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/integrations/${id}/test`));
  },

  syncSOARIntegration: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/soar/integrations/${id}/sync`));
  },

  getSOARIntegrationTypes: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/integration-types'));
  },

  // ========================================================================
  // Identity Threat Detection - Impossible Travel (port 8058)
  // ========================================================================

  getImpossibleTravelEvents: async (params?: { severity?: string; status?: string; time_range?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/impossible-travel', params as any));
  },

  getImpossibleTravelEvent: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/impossible-travel/${id}`));
  },

  checkImpossibleTravel: async (data: { user_id: string; source_ip: string; source_location?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/identity-threats/impossible-travel/check', data));
  },

  // ========================================================================
  // Identity Threat Detection - Privilege Escalation Paths
  // ========================================================================

  getPrivilegeEscalationPaths: async (params?: { severity?: string; cloud_provider?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/privilege-escalation-paths', params as any));
  },

  getPrivilegeEscalationPath: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/privilege-escalation-paths/${id}`));
  },

  // ========================================================================
  // Identity Threat Detection - Credential Compromise Indicators
  // ========================================================================

  getCredentialCompromiseIndicators: async (params?: { severity?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/credential-compromise-indicators', params as any));
  },

  getCredentialCompromiseIndicator: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/credential-compromise-indicators/${id}`));
  },

  remediateCredentialCompromise: async (id: string, data?: { actions?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/identity-threats/credential-compromise-indicators/${id}/remediate`, data));
  },

  // ========================================================================
  // Identity Threat Detection - Dashboard & Detection Rules
  // ========================================================================

  getIdentityThreatDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/identity-threats/identity-threat-dashboard'));
  },

  getIdentityDetectionRules: async (params?: { status?: string; category?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/detection-rules', params as any));
  },

  getIdentityDetectionRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/detection-rules/${id}`));
  },

  createIdentityDetectionRule: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/identity-threats/detection-rules', data));
  },

  updateIdentityDetectionRule: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/identity-threats/detection-rules/${id}`, data));
  },

  toggleIdentityDetectionRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/identity-threats/detection-rules/${id}/toggle`));
  },

  deleteIdentityDetectionRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/identity-threats/detection-rules/${id}`));
  },

  // ========================================================================
  // Identity Threat Detection - Core (port 8058)
  // ========================================================================

  getIdentityThreats: async (params?: { severity?: string; status?: string; threat_type?: string; time_range?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/threats', params as any));
  },

  getIdentityThreat: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/threats/${id}`));
  },

  getIdentityAnomalies: async (params?: { type?: string; risk_level?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/anomalies', params as any));
  },

  getIdentityRiskScores: async (params?: { min_score?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/risk-scores', params as any));
  },

  getIdentityRiskScore: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/identity-threats/risk-scores/${identityId}`));
  },

  getCompromisedCredentials: async (params?: { status?: string; time_range?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/compromised-credentials', params as any));
  },

  getLateralMovementPaths: async (params?: { risk_level?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/lateral-movement', params as any));
  },

  getSessionHijackingEvents: async (params?: { status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/session-hijacking', params as any));
  },

  getIdentityThreatTimeline: async (identityId: string, params?: { time_range?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/cdr/identity-threats/timeline/${identityId}`, params as any));
  },

  getIdentityThreatStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/identity-threats/stats'));
  },

  getHighRiskIdentities: async (params?: { limit?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/identity-threats/high-risk-identities', params as any));
  },

  investigateIdentity: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/identity-threats/investigate/${identityId}`));
  },

  // ========================================================================
  // UEBA - User & Entity Behavior Analytics
  // (anomaly-detection service mounts /api/v1/ueba/* — gateway rewrites /cdr/ueba → /ueba)
  // ========================================================================
  getUEBAScores: async (params?: { entity_type?: string; min_score?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/ueba/scores', params as any));
  },
  getUEBAEntityScore: async (entityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/ueba/scores/${entityId}`));
  },
  getUEBATopRisks: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/ueba/top-risks', params as any));
  },
  recalculateUEBA: async (data?: { entity_id?: string; lookback_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/ueba/recalculate', data || {}));
  },
  computeUEBA: async (data: { entity_id: string; events?: any[]; lookback_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/ueba/compute', data));
  },
  getUEBAModels: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/anomalies', { type: 'models' } as any));
  },
  getUEBAPeerGroups: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/anomalies', { type: 'peer-groups' } as any));
  },

  // ========================================================================
  // R86 — Detection Latency Telemetry (Wiz SLA: detect within 60s)
  // ========================================================================
  getDetectionLatencyStats: async (params?: { window_hours?: number; sla_seconds?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/detections/latency-stats', params as any));
  },

  // ========================================================================
  // Response Actions (soar-automation service catalog + execute + rollback)
  // ========================================================================
  getResponseActionCatalog: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/response-actions/catalog'));
  },
  executeResponseAction: async (data: { action_type: string; target_type: string; target_id: string; parameters?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/response-actions', data));
  },
  rollbackResponseAction: async (actionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/response-actions/${actionId}/rollback`, {}));
  },

  // ========================================================================
  // SOAR - Workflows, Approvals, Webhooks
  // ========================================================================
  getSOARWorkflows: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/workflows'));
  },
  getSOARApprovalQueue: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/cdr/soar/approval-queue'));
  },

  // ========================================================================
  // Runtime Rules (Custom eBPF rules)
  // ========================================================================
  getRuntimeRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/runtime-rules'));
  },
  createRuntimeRule: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/runtime-rules', data));
  },
  updateRuntimeRule: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/runtime-rules/${id}`, data));
  },
  deleteRuntimeRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/cdr/runtime-rules/${id}`));
  },
  testRuntimeRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/cdr/runtime-rules/${id}/test`));
  },

  // ========================================================================
  // Sensor Management
  // ========================================================================
  getSensors: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/sensors'));
  },
  registerSensor: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/sensors', data));
  },
  getSensorCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/sensors/coverage'));
  },

  // ========================================================================
  // Response Policies
  // ========================================================================
  getResponsePolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/response-policies'));
  },
  createResponsePolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/response-policies', data));
  },
  updateResponsePolicy: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/cdr/response-policies/${id}`, data));
  },

  // ========================================================================
  // Signal Correlation
  // ========================================================================
  getSignalCorrelations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/signal-correlation'));
  },
  reCorrelateSignals: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/signal-correlation/re-correlate'));
  },
  getCorrelationDetail: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/signal-correlation/${id}`));
  },

  // ========================================================================
  // Detection Issues (Detection-to-Issue lifecycle)
  // ========================================================================
  getDetectionIssues: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/detection-issues', params as any));
  },
  promoteDetection: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/detection-issues', data));
  },
  updateDetectionIssue: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.patch<any>(`/api/v1/cdr/detection-issues/${id}`, data));
  },
  // Create a real external ticket via the canonical notification-service Jira
  // integration. Fails (HTTP 400) when no Jira integration is configured, so
  // callers must only record a ticket id when this resolves with an issue_key.
  createJiraTicket: async (data: {
    title: string; severity?: string; description?: string;
    alert_id?: string; finding_id?: string; labels?: string[]; assignee?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/integrations/jira/create-ticket', data));
  },

  // ========================================================================
  // FIM (File Integrity Monitoring)
  // ========================================================================
  getFIMEvents: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/fim'));
  },
  getFIMPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/fim/policies'));
  },
  createFIMPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/fim/policies', data));
  },

  // ========================================================================
  // Behavioral Baselines
  // ========================================================================
  getBaselines: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/baselines'));
  },
  recalculateBaselines: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/cdr/baselines/recalculate'));
  },

  // ========================================================================
  // MITRE ATT&CK Coverage
  // ========================================================================
  getMITRECoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/mitre-coverage'));
  },

  // ========================================================================
  // Investigation Graph
  // ========================================================================
  getInvestigationGraph: async (incidentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/investigation-graph/${incidentId}`));
  },

  // ========================================================================
  // Threat Center
  // ========================================================================
  getThreatCenter: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cdr/threat-center'));
  },
  getThreatDetail: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cdr/threat-center/${id}`));
  },
};

// ============================================================================
// Real-time CSPM Detection API
// ============================================================================

export const realtimeCSPMApi = {
  getRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/realtime/rules'));
  },

  getRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cdr/realtime/rules/${id}`));
  },

  // Rule toggle. The gateway rewrites /cdr/realtime/rules → /detection/rules
  // (cloud-log-analytics), whose toggle handler is PUT (main.py:3995). A POST
  // here returned 405 and the caller then optimistically flipped local state
  // (false success). Use PUT so the flip reflects the real persisted state.
  toggleRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/cdr/realtime/rules/${id}/toggle`));
  },

  ingestEvent: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cdr/realtime/events', data));
  },

  // Real normalized cloud events (CloudTrail / Azure Activity / GCP Audit).
  // /cdr/events → gateway rewrite → /api/v1/events (cloud-log-analytics,
  // DB-first csp_cdr.cloud_events). Returns { events, total, time_range_hours }.
  getEvents: async (params?: { provider?: string; service?: string; action?: string; identity?: string; time_range?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/events', params as any));
  },

  // Real configured cloud log sources. /cdr/log-analytics/log-sources →
  // gateway rewrite → /api/v1/log-sources (DB-first csp_cdr.log_sources).
  // Returns { log_sources, total }.
  getSources: async (params?: { provider?: string; enabled?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/log-analytics/log-sources', params as any));
  },

  getDetections: async (params?: { severity?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/realtime/detections', params));
  },

  // NOTE: /cdr/realtime/stats is gateway-aliased to the detection RULES list
  // (no native stats route), so events_per_hour / active_detections etc. are
  // undefined in its response. Callers should compute stats from the real
  // rules + detections + sources feeds rather than trusting this shape.
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/realtime/stats'));
  },
};

// ============================================================================
// CDR SIEM Export API (port 8116) — moved from CSPM to CDR
// ============================================================================

export const siemExportApi = {
  getConnectors: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/siem/connectors'));
  },

  getConnector: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cdr/siem/connectors/${id}`));
  },

  createConnector: async (data: { type: string; name: string; config: any }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cdr/siem/connectors', data));
  },

  updateConnector: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/cdr/siem/connectors/${id}`, data));
  },

  deleteConnector: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/cdr/siem/connectors/${id}`));
  },

  testConnector: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cdr/siem/connectors/${id}/test`));
  },

  toggleConnector: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cdr/siem/connectors/${id}/toggle`));
  },

  exportFindings: async (data: { connector_id: string; finding_ids?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cdr/siem/export', data));
  },

  getExportHistory: async (params?: { connector_id?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/siem/export/history', params));
  },

  getWebhooks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cdr/siem/webhooks'));
  },

  createWebhook: async (data: { name: string; url: string; events: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cdr/siem/webhooks', data));
  },
};

// ============================================================================
// Event Stream API
// ============================================================================

export const eventStreamApi = {
  getConfigs: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/event-stream/config'));
  },
  createConfig: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/event-stream/config', data));
  },
  deleteConfig: async (configId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/event-stream/config/${configId}`));
  },
  ingestEvent: async (event: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/event-stream/ingest', event));
  },
  getRecentEvents: async (limit?: number): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/event-stream/events', { limit }));
  },
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/event-stream/stats'));
  },
};

// ============================================================================
// CDR Log Analytics API
// ============================================================================

export const cdrLogAnalyticsApi = {
  // Log Sources
  getLogSources: (params?: { provider?: string; enabled?: boolean }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/log-sources', params).then(_thenUnwrap),
  createLogSource: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/log-sources', data).then(_thenUnwrap),
  toggleLogSource: (sourceId: string) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/log-sources/${sourceId}/toggle`).then(_thenUnwrap),

  // Events
  getEvents: (params?: { provider?: string; service?: string; action?: string; identity?: string; time_range?: string; limit?: number; offset?: number }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/events', params).then(_thenUnwrap),
  searchEvents: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/events/search', data).then(_thenUnwrap),
  aggregateEvents: (params?: { field?: string; time_range?: string }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/events/aggregate', params).then(_thenUnwrap),
  getEventTimeline: (params?: { interval?: string; time_range?: string }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/events/timeline', params).then(_thenUnwrap),

  // Ingestion
  getIngestionStats: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/ingestion/statistics').then(_thenUnwrap),

  // Cloud Alerts (GuardDuty, Azure Defender, GCP SCC)
  getCloudAlerts: (params?: { provider?: string; severity?: string; status?: string }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/cloud-alerts', params).then(_thenUnwrap),

  // Detection / Sigma Rules
  getDetectionRules: (params?: { enabled?: boolean; severity?: string }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/detection/rules', params).then(_thenUnwrap),
  toggleDetectionRule: (ruleId: string) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/detection/rules/${ruleId}/toggle`).then(_thenUnwrap),
  getSigmaRules: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/sigma-rules').then(_thenUnwrap),

  // Retention
  getRetentionPolicies: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/retention/policies').then(_thenUnwrap),

  // Compliance
  getComplianceCoverage: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/compliance/coverage-report').then(_thenUnwrap),

  // Detection Rules CRUD
  getDetectionRule: (ruleId: string) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/detection/rules/${ruleId}`).then(_thenUnwrap),
  createDetectionRule: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/detection/rules', data).then(_thenUnwrap),
  deleteDetectionRule: (ruleId: string) =>
    fetchClient.delete(`/api/v1/cdr/log-analytics/detection/rules/${ruleId}`).then(_thenUnwrap),
  testDetectionRule: (ruleId: string, data?: any) =>
    fetchClient.post(`/api/v1/cdr/log-analytics/detection/rules/${ruleId}/test`, data).then(_thenUnwrap),

  // Detection Alerts
  getDetectionAlerts: (params?: { severity?: string; acknowledged?: boolean; limit?: number }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/detection/alerts', params).then(_thenUnwrap),
  getDetectionAlert: (alertId: string) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/detection/alerts/${alertId}`).then(_thenUnwrap),
  acknowledgeAlert: (alertId: string) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/detection/alerts/${alertId}/acknowledge`).then(_thenUnwrap),
  markAlertFalsePositive: (alertId: string) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/detection/alerts/${alertId}/false-positive`).then(_thenUnwrap),

  // Log Source Detail
  getLogSource: (sourceId: string) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/log-sources/${sourceId}`).then(_thenUnwrap),

  // Event Detail
  getEvent: (eventId: string) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/events/${eventId}`).then(_thenUnwrap),

  // Parsers
  getParsers: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/parsers').then(_thenUnwrap),

  // Retention update
  updateRetentionPolicy: (policyId: string, data: any) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/retention/policies/${policyId}`, data).then(_thenUnwrap),

  // Risk Posture
  getRiskPosture: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/risk-posture').then(_thenUnwrap),

  // Dashboard
  getDashboard: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/dashboard').then(_thenUnwrap),

  // OCSF Normalization
  normalizeOCSF: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/events/normalize/ocsf', data).then(_thenUnwrap),

  // Event Ingest
  ingestEvents: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/events/ingest', data).then(_thenUnwrap),

  // Auto-discover log sources
  autoDiscoverLogSources: (data?: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/log-sources/auto-discover', data).then(_thenUnwrap),

  // Detection Rules Library
  getDetectionRulesLibrary: (params?: { category?: string; provider?: string }) =>
    fetchClient.get('/api/v1/cdr/log-analytics/detection-rules/library', params).then(_thenUnwrap),

  // K8s Audit Logs
  createK8sAuditSource: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/log-sources/k8s-audit', data).then(_thenUnwrap),
  getK8sAuditSources: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/log-sources/k8s-audit').then(_thenUnwrap),
  getK8sAuditEvents: (sourceId: string, params?: { limit?: number }) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/log-sources/k8s-audit/${sourceId}/events`, params).then(_thenUnwrap),
  getK8sAuditDetections: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/log-sources/k8s-audit/detections').then(_thenUnwrap),

  // Dedup Stats
  getDedupStats: () =>
    fetchClient.get('/api/v1/cdr/log-analytics/events/dedup/stats').then(_thenUnwrap),
  resetDedup: () =>
    fetchClient.post('/api/v1/cdr/log-analytics/events/dedup/reset').then(_thenUnwrap),

  // Sigma Rules CRUD (enhanced)
  createSigmaRule: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/sigma-rules', data).then(_thenUnwrap),
  getSigmaRule: (ruleId: string) =>
    fetchClient.get(`/api/v1/cdr/log-analytics/sigma-rules/${ruleId}`).then(_thenUnwrap),
  updateSigmaRule: (ruleId: string, data: any) =>
    fetchClient.put(`/api/v1/cdr/log-analytics/sigma-rules/${ruleId}`, data).then(_thenUnwrap),
  testSigmaRule: (ruleId: string, data?: any) =>
    fetchClient.post(`/api/v1/cdr/log-analytics/sigma-rules/${ruleId}/test`, data).then(_thenUnwrap),
  importSigmaRules: (data: any) =>
    fetchClient.post('/api/v1/cdr/log-analytics/sigma-rules/import', data).then(_thenUnwrap),
};

// ── CDR Advanced Detection & Query API ──
export const cdrAdvancedApi = {
  // Detection-as-Code pipelines
  getPipelines: () =>
    fetchClient.get('/api/v1/detection-as-code/pipelines').then(_thenUnwrap),
  createPipeline: (data: { name: string; repo_url?: string; branch?: string; rules_path?: string; auto_deploy?: boolean }) =>
    fetchClient.post('/api/v1/detection-as-code/pipelines', data).then(_thenUnwrap),
  // Saved log queries
  getSavedQueries: () =>
    fetchClient.get('/api/v1/queries/saved').then(_thenUnwrap),
  createSavedQuery: (data: { name: string; query: string; description?: string; tags?: string[] }) =>
    fetchClient.post('/api/v1/queries/saved', data).then(_thenUnwrap),
  deleteSavedQuery: (id: string) =>
    fetchClient.delete(`/api/v1/queries/saved/${id}`).then(_thenUnwrap),
  // Log query execution
  executeQuery: (data: { query: string; time_range?: string; limit?: number }) =>
    fetchClient.post('/api/v1/query/execute', data).then(_thenUnwrap),
  // Query validation
  validateQuery: (data: { query: string }) =>
    fetchClient.post('/api/v1/query/validate', data).then(_thenUnwrap),
  // SIEM connectors
  getSIEMConnectors: () =>
    fetchClient.get('/api/v1/siem/connectors').then(_thenUnwrap),
  createSIEMConnector: (data: { name: string; type: string; config: Record<string, any> }) =>
    fetchClient.post('/api/v1/siem/connectors', data).then(_thenUnwrap),
  // Sigma rule import
  importSigmaRules: (data: { rules: any[]; source?: string }) =>
    fetchClient.post('/api/v1/sigma-rules/import', data).then(_thenUnwrap),
  // Compliance rule mappings
  getComplianceRuleMappings: () =>
    fetchClient.get('/api/v1/compliance/rule-mappings').then(_thenUnwrap),
  createComplianceRuleMapping: (data: any) =>
    fetchClient.post('/api/v1/compliance/rule-mappings', data).then(_thenUnwrap),
  getComplianceRuleMapping: (mappingId: string) =>
    fetchClient.get(`/api/v1/compliance/rule-mappings/${mappingId}`).then(_thenUnwrap),
  getComplianceCoverageReport: () =>
    fetchClient.get('/api/v1/compliance/coverage-report').then(_thenUnwrap),

  // Query language
  getQuerySyntax: () =>
    fetchClient.get('/api/v1/query/syntax').then(_thenUnwrap),
  getQuerySuggestions: (params: { partial: string }) =>
    fetchClient.get('/api/v1/query/suggestions', params).then(_thenUnwrap),
  getQueryTemplates: () =>
    fetchClient.get('/api/v1/query/templates').then(_thenUnwrap),
  saveQuery: (data: { name: string; query: string; description?: string }) =>
    fetchClient.post('/api/v1/query/save', data).then(_thenUnwrap),
  executeSavedQuery: (queryId: string) =>
    fetchClient.post(`/api/v1/queries/saved/${queryId}/execute`).then(_thenUnwrap),
  getSavedQuery: (queryId: string) =>
    fetchClient.get(`/api/v1/queries/saved/${queryId}`).then(_thenUnwrap),

  // Sigma evaluate (R95: BE takes rule_id + time_range_hours + limit as
  // query params, not body — primitives without Body() default to query).
  evaluateSigma: (data: { rule_id: string; time_range_hours?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    qs.set('rule_id', data.rule_id);
    if (data.time_range_hours != null) qs.set('time_range_hours', String(data.time_range_hours));
    if (data.limit != null) qs.set('limit', String(data.limit));
    return fetchClient.post(`/api/v1/sigma/evaluate?${qs.toString()}`).then(_thenUnwrap);
  },
  evaluateAllSigma: (data: { time_range_hours?: number }) => {
    const qs = new URLSearchParams();
    if (data?.time_range_hours != null) qs.set('time_range_hours', String(data.time_range_hours));
    const url = qs.toString() ? `/api/v1/sigma/evaluate-all?${qs.toString()}` : '/api/v1/sigma/evaluate-all';
    return fetchClient.post(url).then(_thenUnwrap);
  },

  // Run detection rules
  runDetectionRules: (data: any) =>
    fetchClient.post('/api/v1/rules/run', data).then(_thenUnwrap),

  // DAC (Detection-as-Code) repositories
  getDACRepositories: () =>
    fetchClient.get('/api/v1/dac/repositories').then(_thenUnwrap),
  createDACRepository: (data: any) =>
    fetchClient.post('/api/v1/dac/repositories', data).then(_thenUnwrap),
  syncDACRepository: (repoId: string) =>
    fetchClient.post(`/api/v1/dac/repositories/${repoId}/sync`).then(_thenUnwrap),
  getDACPipelineRuns: (params?: { repo_id?: string; status?: string }) =>
    fetchClient.get('/api/v1/dac/pipeline/runs', params).then(_thenUnwrap),
  getDACPipelineStatus: () =>
    fetchClient.get('/api/v1/dac/pipeline/status').then(_thenUnwrap),

  // SIEM connectors (enhanced)
  getSIEMConnector: (connectorId: string) =>
    fetchClient.get(`/api/v1/siem/connectors/${connectorId}`).then(_thenUnwrap),
  testSIEMConnector: (connectorId: string) =>
    fetchClient.post(`/api/v1/siem/connectors/${connectorId}/test`).then(_thenUnwrap),
  deleteSIEMConnector: (connectorId: string) =>
    fetchClient.delete(`/api/v1/siem/connectors/${connectorId}`).then(_thenUnwrap),
  exportToSIEM: (data: { connector_id: string; query?: string; time_range?: string }) =>
    fetchClient.post('/api/v1/siem/export', data).then(_thenUnwrap),
  getSIEMExportJobs: (params?: { connector_id?: string; status?: string }) =>
    fetchClient.get('/api/v1/siem/export/jobs', params).then(_thenUnwrap),

  // Normalization
  getNormalizationSchemas: () =>
    fetchClient.get('/api/v1/normalization/schemas').then(_thenUnwrap),
  transformNormalization: (data: any) =>
    fetchClient.post('/api/v1/normalization/transform', data).then(_thenUnwrap),
  getNormalizationMappings: () =>
    fetchClient.get('/api/v1/normalization/mappings').then(_thenUnwrap),
  createCustomParser: (data: any) =>
    fetchClient.post('/api/v1/normalization/custom-parser', data).then(_thenUnwrap),

  // Event streaming
  streamEvents: (data: any) =>
    fetchClient.post('/api/v1/events/stream', data).then(_thenUnwrap),

  // CloudTrail ingest
  ingestCloudTrail: (data: any) =>
    fetchClient.post('/api/v1/cdr/ingest-cloudtrail', data).then(_thenUnwrap),
};

// ============================================================================
// Threat Intelligence Feeds API
// ============================================================================

export const threatIntelFeedsApi = {
  // Feed Management
  createFeed: (data: { name: string; source: string; description?: string; api_key?: string; sync_interval_minutes?: number; enabled?: boolean; config?: Record<string, unknown> }) =>
    fetchClient.post('/api/v1/threat-intel/feeds', data).then(_thenUnwrap),
  listFeeds: () =>
    fetchClient.get('/api/v1/threat-intel/feeds').then(_thenUnwrap),
  updateFeed: (feedId: string, data: { name?: string; description?: string; api_key?: string; sync_interval_minutes?: number; enabled?: boolean; config?: Record<string, unknown> }) =>
    fetchClient.put(`/api/v1/threat-intel/feeds/${feedId}`, data).then(_thenUnwrap),
  deleteFeed: (feedId: string) =>
    fetchClient.delete(`/api/v1/threat-intel/feeds/${feedId}`).then(_thenUnwrap),
  syncFeed: (feedId: string) =>
    fetchClient.post(`/api/v1/threat-intel/feeds/${feedId}/sync`).then(_thenUnwrap),
  getFeedStatus: (feedId: string) =>
    fetchClient.get(`/api/v1/threat-intel/feeds/${feedId}/status`).then(_thenUnwrap),

  // IOCs — the bare /api/v1/threat-intel/iocs* prefix routes to a service that
  // doesn't mount these handlers in the monolith (404). The CDR umbrella mounts
  // the threat-intelligence router under /api/v1/cdr/threat-intel/iocs*, which
  // returns real IOCs + stats — so target that (matches the getIOCs wrapper).
  listIOCs: (params?: { ioc_type?: string; source?: string; severity?: string; active_only?: boolean; search?: string; limit?: number; offset?: number }) =>
    fetchClient.get('/api/v1/cdr/threat-intel/iocs', params).then(_thenUnwrap),
  searchIOCs: (data: { value: string; ioc_type?: string }) =>
    fetchClient.post('/api/v1/cdr/threat-intel/iocs/search', data).then(_thenUnwrap),
  bulkCheckIOCs: (data: { indicators: string[]; indicator_type?: string }) =>
    fetchClient.post('/api/v1/cdr/threat-intel/iocs/bulk-check', data).then(_thenUnwrap),
  getIOCStats: () =>
    fetchClient.get('/api/v1/cdr/threat-intel/iocs/stats').then(_thenUnwrap),

  // Enrichment
  enrichIP: (ip: string) =>
    fetchClient.post('/api/v1/threat-intel/enrich/ip', { ip }).then(_thenUnwrap),
  enrichDomain: (domain: string) =>
    fetchClient.post('/api/v1/threat-intel/enrich/domain', { domain }).then(_thenUnwrap),
  enrichHash: (hash: string, hashType?: string) =>
    fetchClient.post('/api/v1/threat-intel/enrich/hash', { hash, hash_type: hashType || 'sha256' }).then(_thenUnwrap),
  enrichFinding: (findingId: string) =>
    fetchClient.get(`/api/v1/threat-intel/enrich/finding/${findingId}`).then(_thenUnwrap),

  // MITRE ATT&CK
  getMITRETactics: () =>
    fetchClient.get('/api/v1/threat-intel/mitre/tactics').then(_thenUnwrap),
  getMITRETechniques: (params?: { tactic?: string; search?: string; limit?: number; offset?: number }) =>
    fetchClient.get('/api/v1/threat-intel/mitre/techniques', params).then(_thenUnwrap),
  getMITRECoverage: () =>
    fetchClient.get('/api/v1/threat-intel/mitre/coverage').then(_thenUnwrap),
  mapFindingToMITRE: (data: { finding_id: string; finding_title?: string; finding_description?: string; resource_type?: string }) =>
    fetchClient.post('/api/v1/threat-intel/mitre/map-finding', data).then(_thenUnwrap),
};

// ============================================================================
// CDR Prevention/Blocking Mode
// ============================================================================

export const preventionApi = {
  // Policies
  getPolicies: (params?: { severity?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/cdr/prevention/policies', params).then(_thenUnwrap),
  // NOTE: body shape mirrors the backend PreventionPolicyCreate model
  // (realtime-detection service — csp_cdr.prevention_policies). `mode` is one of
  // monitor|simulate|enforce; `prevention_actions` are validated action_types.
  createPolicy: (data: {
    name: string; description?: string; enabled?: boolean; mode?: string;
    provider?: string; severity_threshold?: string; prevention_actions?: string[];
    rule_ids?: string[]; scope?: Record<string, unknown>; conditions?: Record<string, unknown>;
  }) =>
    fetchClient.post('/api/v1/cdr/prevention/policies', data).then(_thenUnwrap),
  updatePolicy: (policyId: string, data: {
    name?: string; description?: string; enabled?: boolean; mode?: string;
    provider?: string; severity_threshold?: string; prevention_actions?: string[];
    rule_ids?: string[]; scope?: Record<string, unknown>; conditions?: Record<string, unknown>;
  }) =>
    fetchClient.put(`/api/v1/cdr/prevention/policies/${policyId}`, data).then(_thenUnwrap),
  deletePolicy: (policyId: string) =>
    fetchClient.delete(`/api/v1/cdr/prevention/policies/${policyId}`).then(_thenUnwrap),
  togglePolicy: (policyId: string, data: { enabled: boolean }) =>
    fetchClient.post(`/api/v1/cdr/prevention/policies/${policyId}/toggle`, data).then(_thenUnwrap),

  // Actions & Executions
  getActions: (params?: { policy_id?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/cdr/prevention/actions', params).then(_thenUnwrap),
  getExecutions: (params?: { policy_id?: string; action_id?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/cdr/prevention/executions', params).then(_thenUnwrap),
  // Dry-run a single prevention action (no cloud mutation). Mirrors the BE
  // PreventionSimulateRequest (realtime-detection main.py:6030): action_type +
  // target_resource + params; returns an execution record with status
  // "simulated". Use this BEFORE execute() to preview the effect.
  simulateAction: (data: {
    action_type: string; target_resource: string; target_account?: string;
    provider?: string; params?: Record<string, unknown>;
    detection_id?: string; event_id?: string;
  }) =>
    fetchClient.post('/api/v1/cdr/prevention/simulate', data).then(_thenUnwrap),
  // Legacy policy-scoped simulate (kept for back-compat callers).
  simulate: (data: { policy_id: string; event: Record<string, unknown> }) =>
    fetchClient.post('/api/v1/cdr/prevention/simulate', data).then(_thenUnwrap),
  // Execute a REAL (non-dry-run) containment action. Mirrors the BE
  // PreventionExecuteRequest (realtime-detection main.py:6083). Approval-gated
  // and fail-closed on the server: real cloud mutation only happens when ops
  // has set CSP_CDR_PREVENTION_EXECUTE_ENABLED=true AND approved_by is named
  // AND confirm=true. When CSP_CDR_PREVENTION_APPROVAL_TOKEN is set, pass it as
  // approvalToken → X-Approval-Token header. Success is only recorded when the
  // underlying SDK call actually returns success (status "success" vs "failed").
  execute: (
    data: {
      action_type: string; target_resource: string; target_account?: string;
      provider?: string; params?: Record<string, unknown>;
      detection_id?: string; event_id?: string; policy_id?: string;
      approved_by: string; confirm: boolean; reason?: string;
    },
    approvalToken?: string,
  ) =>
    fetchClient.request('/api/v1/cdr/prevention/execute', {
      method: 'POST',
      body: data,
      headers: approvalToken ? { 'X-Approval-Token': approvalToken } : {},
    }).then(_thenUnwrap),
  // Block an IOC (IP / domain / hash / url). realtime-detection main.py:6278.
  blockIoc: (data: { type: string; value: string; reason?: string; severity?: string; source?: string; blocked_by?: string; expires_at?: string }) =>
    fetchClient.post('/api/v1/cdr/ioc/block', data).then(_thenUnwrap),
  getBlockedIocs: (params?: { ioc_type?: string; active_only?: boolean; limit?: number; offset?: number }) =>
    fetchClient.get('/api/v1/cdr/ioc/blocklist', params as any).then(_thenUnwrap),
  getActionCatalog: () =>
    fetchClient.get('/api/v1/cdr/prevention/actions').then(_thenUnwrap),
  getStats: () =>
    fetchClient.get('/api/v1/cdr/prevention/stats').then(_thenUnwrap),
};

// ============================================================================
// Forensic Timeline API
// ============================================================================

export const forensicTimelineApi = {
  buildTimeline: (data: any) => fetchClient.post('/api/v1/cdr/forensics/timeline/build', data).then(_thenUnwrap),
  getTimeline: (incidentId: string) => fetchClient.get(`/api/v1/cdr/forensics/timeline/${incidentId}`).then(_thenUnwrap),
  getTimelineEvents: (incidentId: string, params?: any) => fetchClient.get(`/api/v1/cdr/forensics/timeline/${incidentId}/events`, params).then(_thenUnwrap),
  annotateEvent: (incidentId: string, data: any) => fetchClient.post(`/api/v1/cdr/forensics/timeline/${incidentId}/annotate`, data).then(_thenUnwrap),
  getEntities: (incidentId: string) => fetchClient.get(`/api/v1/cdr/forensics/timeline/${incidentId}/entities`).then(_thenUnwrap),
  getIOCs: (incidentId: string) => fetchClient.get(`/api/v1/cdr/forensics/timeline/${incidentId}/iocs`).then(_thenUnwrap),
  exportTimeline: (incidentId: string, data: any) => fetchClient.post(`/api/v1/cdr/forensics/timeline/${incidentId}/export`, data).then(_thenUnwrap),
  getStats: () => fetchClient.get('/api/v1/cdr/forensics/timeline/stats').then(_thenUnwrap),
};

// ============================================================================
// CIRA / Investigation API
// ============================================================================

export const investigationApi = {
  create: (data: any) => fetchClient.post('/api/v1/investigations', data).then(_thenUnwrap),
  list: (params?: any) => fetchClient.get('/api/v1/investigations', params).then(_thenUnwrap),
  get: (id: string) => fetchClient.get(`/api/v1/investigations/${id}`).then(_thenUnwrap),
  getEvidence: (id: string) => fetchClient.get(`/api/v1/investigations/${id}/evidence`).then(_thenUnwrap),
  getTimeline: (id: string) => fetchClient.get(`/api/v1/investigations/${id}/timeline`).then(_thenUnwrap),
  executeContainment: (id: string, data: any) => fetchClient.post(`/api/v1/investigations/${id}/containment`, data).then(_thenUnwrap),
  getPlaybooks: () => fetchClient.get('/api/v1/investigations/playbooks').then(_thenUnwrap),
  checkIocs: (data: any) => fetchClient.post('/api/v1/investigations/check-iocs', data).then(_thenUnwrap),
  preserveEvidence: (data: any) => fetchClient.post('/api/v1/investigations/preserve-evidence', data).then(_thenUnwrap),
  analyzeIssue: (data: any) => fetchClient.post('/api/v1/issues/analyze', data).then(_thenUnwrap),
  // R90 — wire remaining endpoints (4 of 14 were previously zero-called)
  getSteps: (id: string) => fetchClient.get(`/api/v1/investigations/${id}/steps`).then(_thenUnwrap),
  // R145 — MITRE auto-map for an ai-secops investigation. {id} is AFTER /mitre
  // so the gateway longer-prefix rule routes to ai-secops-agent:8131 (NOT to
  // the legacy forensics /api/v1/investigations/{id}/mitre on cdr-aggregator,
  // which queries a different data store).
  getMitreMapping: (id: string) => fetchClient.get(`/api/v1/investigations/mitre/${id}`).then(_thenUnwrap),
  getVerdict: (id: string) => fetchClient.get(`/api/v1/investigations/${id}/verdict`).then(_thenUnwrap),
  getVerdictsSummary: () => fetchClient.get('/api/v1/investigations/verdicts/summary').then(_thenUnwrap),
  getConfigRules: () => fetchClient.get('/api/v1/investigations/config/rules').then(_thenUnwrap),
  getConfigServices: () => fetchClient.get('/api/v1/investigations/config/services').then(_thenUnwrap),
  deleteInvestigation: (id: string) => fetchClient.delete(`/api/v1/investigations/${id}`).then(_thenUnwrap),
  override: (id: string, data: { verdict: string; reason: string; actor?: string }) =>
    fetchClient.post(`/api/v1/investigations/${id}/override`, data).then(_thenUnwrap),
  reinvestigate: (id: string) => fetchClient.post(`/api/v1/investigations/${id}/reinvestigate`, {}).then(_thenUnwrap),
  batchInvestigate: (data: { issue_ids: string[]; priority?: string }) =>
    fetchClient.post('/api/v1/investigations/batch', data).then(_thenUnwrap),
};
